import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants/routes';
import { gamePlayService } from '../../services/db';
import Spinner from '../../components/ui/Spinner';
import { SearchIcon, FilterSlidersIcon, BibleIcon } from '../../components/common/Icons';

const PlayGamesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Redirect unauthenticated guests to About Us
  useEffect(() => {
    if (!user) {
      navigate(ROUTES.PUBLIC.HOME, { replace: true });
    }
  }, [user, navigate]);

  // Search & Filter state matching Flutter Seasons.dart
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All'); // 'All' | 'Multiple Contests' | 'Single Contest'
  const [filterVisibility, setFilterVisibility] = useState('All'); // 'All' | 'Public' | 'Private'
  const [filterStatus, setFilterStatus] = useState('All'); // 'All' | 'Active' | 'Completed'
  const [filterFrequency, setFilterFrequency] = useState('All'); // 'All' | 'Daily' | 'Weekly' | 'Monthly'
  const [filterLanguage, setFilterLanguage] = useState('All');
  const [showFilterModal, setShowFilterModal] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadSeasons = async () => {
      setLoading(true);
      try {
        const data = await gamePlayService.getActiveSeasons();
        if (isMounted) setSeasons(data);
      } catch (err) {
        console.error('Failed to load seasons:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadSeasons();
    return () => {
      isMounted = false;
    };
  }, []);

  const activeFiltersCount = [
    filterType !== 'All',
    filterVisibility !== 'All',
    filterStatus !== 'All',
    filterFrequency !== 'All',
    filterLanguage !== 'All',
  ].filter(Boolean).length;

  const clearFilters = () => {
    setFilterType('All');
    setFilterVisibility('All');
    setFilterStatus('All');
    setFilterFrequency('All');
    setFilterLanguage('All');
  };

  const filteredSeasons = seasons.filter((season) => {
    // Strictly require web platform compatibility
    const rawDevices = season.allowed_devices || season.allowedDevices;
    const allowed = Array.isArray(rawDevices)
      ? rawDevices.map((d) => String(d).toLowerCase())
      : typeof rawDevices === 'string'
      ? [rawDevices.toLowerCase()]
      : [];
    if (!allowed.includes('web')) return false;

    if (searchQuery.trim().length > 0) {
      const q = searchQuery.toLowerCase();
      const matchTitle = (season.season || '').toLowerCase().includes(q);
      const matchHost = (season.host || '').toLowerCase().includes(q);
      const matchDesc = (season.description || season.discription || '').toLowerCase().includes(q);
      if (!matchTitle && !matchHost && !matchDesc) return false;
    }

    if (filterStatus !== 'All') {
      const status = (season.status || '').toLowerCase();
      if (filterStatus === 'Active' && status !== 'active') return false;
      if (filterStatus === 'Completed' && status !== 'completed') return false;
    }

    if (filterType !== 'All') {
      if ((season.type || '').toLowerCase() !== filterType.toLowerCase()) return false;
    }

    if (filterVisibility !== 'All') {
      if ((season.visibility || '').toLowerCase() !== filterVisibility.toLowerCase()) return false;
    }

    if (filterFrequency !== 'All') {
      if ((season.frequency || '').toLowerCase() !== filterFrequency.toLowerCase()) return false;
    }

    if (filterLanguage !== 'All') {
      const langs = Array.isArray(season.languages)
        ? season.languages.map((l) => l.toLowerCase())
        : [(season.languages || '').toLowerCase()];
      if (!langs.includes(filterLanguage.toLowerCase())) return false;
    }

    return true;
  });

  return (
    <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', padding: '16px 12px 32px' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        {/* Search & Filter Bar matching Seasons.dart */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2DCD5',
              borderRadius: '24px',
              padding: '8px 16px',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', color: '#8A827C', marginRight: '8px' }}>
              <SearchIcon size={16} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search seasons..."
              style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                fontSize: '14px',
                color: '#332E2A',
                backgroundColor: 'transparent',
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'rgba(51, 46, 42, 0.5)',
                  fontSize: '14px',
                  padding: '2px',
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Filter Button with Count Badge */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowFilterModal(true)}
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2DCD5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#332E2A',
              }}
              title="Filters"
              aria-label="Filter games"
            >
              <FilterSlidersIcon size={18} />
            </button>
            {activeFiltersCount > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  backgroundColor: '#FF5C56',
                  color: '#FFFFFF',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                }}
              >
                {activeFiltersCount}
              </div>
            )}
          </div>
        </div>

        {/* Seasons List matching ListView.builder in Seasons.dart */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Spinner size="lg" />
          </div>
        ) : filteredSeasons.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E2DCD5',
              color: 'rgba(51, 46, 42, 0.65)',
            }}
          >
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(255, 92, 86, 0.08)', color: '#FF5C56', marginBottom: '12px' }}>
              <BibleIcon size={28} />
            </div>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>
              No matching series found. Try adjusting your filters.
            </p>

            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  marginTop: '12px',
                  background: 'none',
                  border: 'none',
                  color: '#FF5C56',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredSeasons.map((season) => {
              const seasonId = season.season_id || season.id;
              const isActive = (season.status || '').toLowerCase() === 'active';
              const accentColor = isActive ? '#FF5C56' : '#8A827C';
              const initials = season.season ? season.season[0].toUpperCase() : 'S';

              const metaParts = [];
              if (season.type) metaParts.push(season.type);
              if (season.frequency) metaParts.push(season.frequency);
              if (season.country) metaParts.push(season.country);
              if (season.languages) {
                metaParts.push(Array.isArray(season.languages) ? season.languages.join(', ') : season.languages);
              }

              return (
                <div
                  key={seasonId}
                  onClick={() => navigate(`/play/season/${seasonId}`)}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid rgba(51, 46, 42, 0.08)',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  {/* Initials Box Avatar matching Seasons.dart (48x48 rounded 12px, #F1ECE6) */}
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      backgroundColor: '#F1ECE6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#332E2A',
                      marginRight: '16px',
                      flexShrink: 0,
                    }}
                  >
                    {season.logo ? (
                      <img
                        src={season.logo}
                        alt={season.season}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      initials
                    )}
                  </div>

                  {/* Season Information */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Title & Status Pill */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#332E2A',
                          lineHeight: 1.25,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {season.season}
                      </div>

                      <div
                        style={{
                          padding: '3px 8px',
                          borderRadius: '100px',
                          backgroundColor: `${accentColor}14`,
                          color: accentColor,
                          fontSize: '11px',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        {isActive ? 'Ongoing' : 'Completed'}
                      </div>
                    </div>

                    {/* Host & Visibility Details */}
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'rgba(51, 46, 42, 0.65)',
                        marginTop: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '6px',
                      }}
                    >
                      <span>By {season.host || 'Ministry Host'}</span>
                      {season.visibility && (
                        <>
                          <span style={{ fontSize: '6px', opacity: 0.5 }}>●</span>
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 'bold',
                              color: 'rgba(51, 46, 42, 0.55)',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {season.visibility.toUpperCase()}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Meta tags summary */}
                    {metaParts.length > 0 && (
                      <div
                        style={{
                          fontSize: '11.5px',
                          color: 'rgba(51, 46, 42, 0.5)',
                          marginTop: '4px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {metaParts.join(' • ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter Modal / Bottom Sheet matching _showFilterBottomSheet in Seasons.dart */}
        {showFilterModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              zIndex: 100,
              padding: '16px',
            }}
            onClick={() => setShowFilterModal(false)}
          >
            <div
              style={{
                backgroundColor: '#FBF6EF',
                borderRadius: '24px 24px 16px 16px',
                width: '100%',
                maxWidth: '520px',
                padding: '24px',
                maxHeight: '85vh',
                overflowY: 'auto',
                boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.15)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle */}
              <div
                style={{
                  width: '40px',
                  height: '4px',
                  backgroundColor: '#E2DCD5',
                  borderRadius: '2px',
                  margin: '0 auto 20px',
                }}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#332E2A', margin: 0 }}>
                  Filter Seasons
                </h3>
                {activeFiltersCount > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#FF5C56',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Status */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#332E2A', marginBottom: '8px' }}>
                  Status
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['All', 'Active', 'Completed'].map((opt) => (
                    <ChoiceChip
                      key={opt}
                      label={opt}
                      selected={filterStatus === opt}
                      onClick={() => setFilterStatus(opt)}
                    />
                  ))}
                </div>
              </div>

              {/* Type */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#332E2A', marginBottom: '8px' }}>
                  Type
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['All', 'Multiple Contests', 'Single Contest'].map((opt) => (
                    <ChoiceChip
                      key={opt}
                      label={opt}
                      selected={filterType === opt}
                      onClick={() => setFilterType(opt)}
                    />
                  ))}
                </div>
              </div>

              {/* Visibility */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#332E2A', marginBottom: '8px' }}>
                  Visibility
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['All', 'Public', 'Private'].map((opt) => (
                    <ChoiceChip
                      key={opt}
                      label={opt}
                      selected={filterVisibility === opt}
                      onClick={() => setFilterVisibility(opt)}
                    />
                  ))}
                </div>
              </div>

              {/* Frequency */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#332E2A', marginBottom: '8px' }}>
                  Frequency
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['All', 'Daily', 'Weekly', 'Monthly'].map((opt) => (
                    <ChoiceChip
                      key={opt}
                      label={opt}
                      selected={filterFrequency === opt}
                      onClick={() => setFilterFrequency(opt)}
                    />
                  ))}
                </div>
              </div>

              {/* Apply Button */}
              <button
                type="button"
                onClick={() => setShowFilterModal(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '25px',
                  backgroundColor: '#FF5C56',
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 92, 86, 0.3)',
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ChoiceChip = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '7px 16px',
      borderRadius: '20px',
      border: `1px solid ${selected ? '#C08552' : '#E2DCD5'}`,
      backgroundColor: selected ? '#C08552' : '#FFFFFF',
      color: selected ? '#FFFFFF' : '#332E2A',
      fontSize: '12.5px',
      fontWeight: selected ? 'bold' : '500',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    }}
  >
    {label}
  </button>
);

export default PlayGamesPage;
